import { getCloudflareContext } from "@opennextjs/cloudflare";
import type { BuyForMe, Shipping, Storage, Wallet } from "./pricing.types";
import type { ShipProcessingFee } from "@/schemas/ship";

type SupportedCountryCode = "GH" | "NG";

type PricingDataCache = {
  buyForMe: Record<SupportedCountryCode, BuyForMe[]>;
  ship: Record<SupportedCountryCode, Shipping[]>;
  wallet: Record<SupportedCountryCode, Wallet[]>;
  storage: Storage | null;
  processingFee: Record<SupportedCountryCode, ShipProcessingFee[]>;
};

const pricingDataCache: PricingDataCache = {
  buyForMe: {
    GH: [],
    NG: [],
  },
  ship: {
    GH: [],
    NG: [],
  },
  wallet: {
    GH: [],
    NG: [],
  },
  storage: null,
  processingFee: {
    GH: [],
    NG: [],
  },
};

const _fetchPricingData = async (endpoint: string) => {
  const response = await fetch(`${process.env.NEXT_PUBLIC_PRICING_API_BASE_URL}${endpoint}`, {
    next: { revalidate: 300 }, // Revalidate every 5 minutes
  });

  if (!response.ok) {
    throw new Error("Failed to fetch pricing data");
  }

  const data = await response.json();
  return data;
};

// Helper to get user country per request
async function getUserCountry(): Promise<SupportedCountryCode> {
  const { cf } = await getCloudflareContext({ async: true });
  return cf?.country === "GH" ? "GH" : "NG";
}

function getOtherCountry(countryCode: SupportedCountryCode): SupportedCountryCode {
  return countryCode === "GH" ? "NG" : "GH";
}

// Helper to get country name
function getCountryName(countryCode: string): string {
  return countryCode === "GH" ? "Ghana" : "Nigeria";
}

const _logError = (message: string, error: unknown) => {
  console.error(message, error instanceof Error ? error.message : String(error));
};

export function getPricingDataCache() {
  return pricingDataCache;
}

export async function fetchBuyForMe() {
  const userCountry = await getUserCountry();
  try {
    const buyForMeData: BuyForMe[] = await _fetchPricingData("/order/order/pricing");
    const userCountryName = getCountryName(userCountry);
    const otherCountry = getOtherCountry(userCountry);
    const otherCountryName = getCountryName(otherCountry);

    pricingDataCache.buyForMe[userCountry] = buyForMeData.filter(
      ({ country }) => country.toLowerCase() === userCountryName.toLowerCase()
    );
    pricingDataCache.buyForMe[otherCountry] = buyForMeData.filter(
      ({ country }) => country.toLowerCase() === otherCountryName.toLowerCase()
    );
    return pricingDataCache.buyForMe[userCountry];
  } catch (error) {
    _logError("Error fetching Buy For Me data:", error);
    return pricingDataCache.buyForMe[userCountry];
  }
}

export async function fetchShipping() {
  const userCountry = await getUserCountry();
  try {
    const otherCountry = getOtherCountry(userCountry);
    const [userCountryShippingData, otherCountryShippingData]: [
      { data: Shipping[] },
      { data: Shipping[] },
    ] = await Promise.all([
      _fetchPricingData(`/ship/pricing/${userCountry}`),
      _fetchPricingData(`/ship/pricing/${otherCountry}`),
    ]);

    pricingDataCache.ship[userCountry] = userCountryShippingData.data;
    pricingDataCache.ship[otherCountry] = otherCountryShippingData.data;
    return pricingDataCache.ship[userCountry];
  } catch (error) {
    _logError("Error fetching Shipping data:", error);
    return pricingDataCache.ship[userCountry];
  }
}

export async function fetchStorage() {
  try {
    const storageData: Storage = await _fetchPricingData(`/store/parcel/pricing`);
    pricingDataCache.storage = storageData;
    return pricingDataCache.storage;
  } catch (error) {
    _logError("Error fetching Storage data:", error);
    return pricingDataCache.storage;
  }
}

export async function fetchWallet() {
  const userCountry = await getUserCountry();
  try {
    const otherCountry = getOtherCountry(userCountry);
    const [userCountryWalletData, otherCountryWalletData]: [
      { data: Wallet[] },
      { data: Wallet[] },
    ] = await Promise.all([
      _fetchPricingData(`/wallet/pricing/${userCountry}`),
      _fetchPricingData(`/wallet/pricing/${otherCountry}`),
    ]);

    pricingDataCache.wallet[userCountry] = userCountryWalletData.data;
    pricingDataCache.wallet[otherCountry] = otherCountryWalletData.data;
    return pricingDataCache.wallet[userCountry];
  } catch (error) {
    _logError("Error fetching Wallet data:", error);
    return pricingDataCache.wallet[userCountry];
  }
}

export const fetchProcessingFee = async () => {
  const userCountry = await getUserCountry();
  try {
    const otherCountry = getOtherCountry(userCountry);
    const [userCountryProcessingFeeData, otherCountryProcessingFeeData]: [
      { data: ShipProcessingFee[] },
      { data: ShipProcessingFee[] },
    ] = await Promise.all([
      _fetchPricingData(`/ship/processingfee/${userCountry}`),
      _fetchPricingData(`/ship/processingfee/${otherCountry}`),
    ]);

    pricingDataCache.processingFee[userCountry] = userCountryProcessingFeeData.data;
    pricingDataCache.processingFee[otherCountry] = otherCountryProcessingFeeData.data;
    return pricingDataCache.processingFee[userCountry];
  } catch (error) {
    _logError("Error fetching Processing Fee data:", error);
    return pricingDataCache.processingFee[userCountry];
  }
};